// Quote lines from the pricing engine and the default descriptions. Used by the
// Intake tab (AccountIntakeTab) and by "Crear propuesta" in the Diagnostic panel
// (diagnostic-admin), so both price a service the same way.
import { PRICING_CONFIG, calculatePrice, formatPrice, quoteLangForCountry } from './pricing.js';
import { getShortDescription, buildCommercialTerms } from './quoteDefaults.js';

/**
 * One line per service: { service, scope, onceFee, monthlyFee, note } (fees as strings,
 * as the Intake tab edits them). keepScope(serviceKey) → an edited scope to keep.
 */
export function buildQuoteLines(countryKey, teamSize, services, keepScope = () => null) {
  const lang = quoteLangForCountry(countryKey);
  const countryName = PRICING_CONFIG.countries[countryKey]?.label || '';
  return services
    .filter(s => s in PRICING_CONFIG.services)
    .map((serviceKey) => {
      const result = calculatePrice(countryKey, serviceKey, teamSize);
      if (!result) return null;
      const svc = PRICING_CONFIG.services[serviceKey];
      const scope = keepScope(serviceKey) || getShortDescription(serviceKey, lang, { teamSize, countryName });
      if (result.isCustom) {
        return { service: svc.label, scope, onceFee: '', monthlyFee: '', note: 'Custom pricing — contact Oike' };
      }
      return {
        service: svc.label,
        scope,
        onceFee: result.isMonthly ? '' : String(result.price),
        monthlyFee: result.isMonthly ? String(result.price) : '',
        note: formatPrice(result, lang),
      };
    })
    .filter(Boolean);
}

/**
 * The fields of a draft quote (Diagnostic Intake › Quote …), with the fees as numbers
 * like "Save Draft". Only fields documented in docs/diagnostic/airtable-schema.md.
 */
export function draftQuoteFields(countryKey, teamSize, services) {
  const country = PRICING_CONFIG.countries[countryKey];
  const lines = buildQuoteLines(countryKey, teamSize, services).map(l => ({
    service: l.service, scope: l.scope,
    onceFee: l.onceFee !== '' ? Number(l.onceFee) : null,
    monthlyFee: l.monthlyFee !== '' ? Number(l.monthlyFee) : null,
    note: l.note,
  }));
  return {
    'Quote Lines JSON': JSON.stringify(lines),
    'Quote Currency': country?.currency || 'USD',
    'Quote Conditions': buildCommercialTerms(services, quoteLangForCountry(countryKey), { teamSize, countryName: country?.label || '' }),
    'Quote Status': 'Draft',
    // The pricing key of the country used (e.g. "bolivia"), as the Intake tab saves it
    'Quote Market': countryKey,
  };
}
